// React
import { gql, useLazyQuery, useMutation, useQuery, useSubscription } from "@apollo/client";
import { useContext, useEffect, useRef, useState } from "react";

// Components
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  Icon,
  MenuItem,
  Modal,
  Select,
  Tooltip,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PedidoCancelacionAdvertencia from "components/PedidoCancelacionAdvertencia";

// Data
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import CustomPagination from "components/Pagination";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DataTable from "components/Tables/DataTable";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { MenuContext } from "context/menuContext";
import { UserContext } from "context/user";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { getUproColor } from "utils/colors";
import { printComanda, printFactura, printTicket } from "utils/comanda";
import { formatMoneyPunto, makeNiceText } from "utils/formatters";
import dataPedidos from "./data/dataPedidos";
import ModalBandejas from "./ModalBandejas";
import ModalCambiarMenu from "./ModalCambiarMenu";
import ModalComprobantes from "./ModalComprobantes";
import ModalDetalles from "./ModalDetalles";
import ModalExcel from "./ModalExcel";
import ModalNuevoPedido from "./ModalNuevoPedido";
import FacturaImpresion from "layouts/pedidos/components/pedidos/FacturaImpresion";
import ModalMenuDelDia from "layouts/productos/ModalMenuDelDia";
import ModalPrecioMenu from "./ModalPrecioMenu";
import Table from "components/Tables/Table";

const notasDeCredito = {
  A: "Nota de crédito A",
  B: "Nota de crédito B",
  V: "Nota de crédito V",
};

const formatTipoCbte = {
  A: 3,
  B: 8,
  V: null,
};

const DEFAULT_TURNO_FORM = {
  id: "",
  nombre: "",
  codigoSistema: null,
  protegido: false,
  bloqueadoPorUso: false,
  bloqueadoEliminacion: false,
  horaInicio: "",
  horaFin: "",
  pedidoDesdeHora: "",
  pedidoDesdeOffsetDias: 0,
  pedidoHastaHora: "",
  pedidoHastaOffsetDias: 0,
  pedidoRelacionDia: "mismo_dia",
  orden: 1,
};

const DEFAULT_FRANJA_FORM = {
  id: "",
  horaInicio: "",
  horaFin: "",
};

const NUEVO_TURNO_ADMIN_ID = "__nuevo_turno_admin__";

const toTimeInput = (value) => String(value || "").slice(0, 5);
const isTurnoProtegidoAdmin = (turno = {}) =>
  Boolean(turno?.protegido) ||
  Boolean(turno?.bloqueadoEliminacion) ||
  ["MEDIODIA", "NOCHE"].includes(String(turno?.codigoSistema || ""));

const ORDEN_LABELS = [
  "Primero",
  "Segundo",
  "Tercero",
  "Cuarto",
  "Quinto",
  "Sexto",
  "Septimo",
  "Octavo",
  "Noveno",
  "Decimo",
];

const toOrdenLabel = (orden) => ORDEN_LABELS[orden - 1] || `${orden}`;

const getApolloErrorMessage = (error, fallbackMessage) => {
  const gqlMessage =
    error?.graphQLErrors?.[0]?.message || error?.networkError?.result?.errors?.[0]?.message;
  const rawMessage = gqlMessage || error?.message;
  if (!rawMessage) return fallbackMessage;
  return String(rawMessage).replace(/^GraphQL error:\s*/i, "");
};

const getPagoRevisionTransferencia = (pedido) =>
  (pedido?.venta?.pagos || []).find(
    (pago) => pago?.estado === "Revision" && pago?.medioDePago?.nombre === "Transferencia"
  ) || null;

function Pedidos() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [openModalBandejas, setOpenModalBandejas] = useState(false);
  const [openModalCambiarMenu, setOpenModalCambiarMenu] = useState(false);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [page, setPage] = useState(1);
  const [modalMenu, setModalMenu] = useState(false);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "id",
    orden: "DESC",
  });
  const [expandFiltroEstado, setExpandFiltroEstado] = useState(false);
  const [filtros, setFiltros] = useState({
    estado: ["PAGO EN REVISIÓN", "RESERVADO"],
  });
  const { menu } = useContext(MenuContext);
  const { user } = useContext(UserContext);
  const puedeCrearTurnosEnSede = Number(user?.sede?.id || user?.idSede || 0) !== 2;
  const { confirmar } = useContext(ConfirmarContext);
  const { handleSnackbar } = useContext(MessageManager);
  const [pedidos, setPedidos] = useState([]);
  const [openModalFactura, setOpenModalFactura] = useState(false); // Estado para controlar el modal
  const [facturaData, setFacturaData] = useState(null); // Datos de la factura
  const [empresaData, setEmpresaData] = useState(null); // Datos de la empresa
  const ultimoNuevoPedidoEventRef = useRef("");
  const ultimoPedidoModificadoEventRef = useRef("");

  const { columns, getRows } = dataPedidos;

  const { data: newData } = useSubscription(
    gql`
      subscription {
        nuevoPedido { id estado createdAt updatedAt turnoReserva }
      }
    `
  );

  const { data: modifiedData } = useSubscription(
    gql`
      subscription {
        pedidoModificado { id estado createdAt updatedAt turnoReserva }
      }
    `
  );

  const [prepararPedido, { loading: loadingSave }] = useMutation(
    gql`
      mutation prepararPedido($id: ID!) {
        prepararPedido(id: $id) {
          id
          detalles {
            numeroComanda
            cantidad
            precio
            comentario
            producto {
              nombre
              seccion
              id
            }
          }
        }
      }
    `
  );

  const [finalizarPedido] = useMutation(
    gql`
      mutation finalizarPedido($id: ID!, $bandejas: Int) {
        finalizarPedido(id: $id, bandejas: $bandejas) {
          id
        }
      }
    `
  );

  const [devolverBandejas] = useMutation(
    gql`
      mutation devolverBandejas($id: ID!) {
        devolverBandejas(id: $id) {
          id
        }
      }
    `
  );


  const [anularPedido] = useMutation(
    gql`
    mutation anularPedido($id: ID!, $confirmacionEliminarPagosAprobados: Boolean) {
      anularPedido(
        id: $id
        confirmacionEliminarPagosAprobados: $confirmacionEliminarPagosAprobados
      ) {
        id
      }
    }
  `
  );

  const [getCancelacionPedidoPreview] = useLazyQuery(
    gql`
    query cancelacionPedidoPreview($id: ID!) {
      cancelacionPedidoPreview(id: $id) {
        tienePagosAprobadosRelacionados
        tieneVentasFacturadas
        mensajeAdvertencia
        pagosAprobados {
          id
          totalPago
          estado
          nombre
          comprobanteFile
        }
      }
    }
  `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const [deleteVenta, { loading: loadingDelete }] = useMutation(
    gql`
      mutation deleteVenta($id: ID!) {
        deleteVenta(id: $id) {
          id
        }
      }
    `
  );

  const [getVenta] = useLazyQuery(
    gql`
      query getVenta($id: ID!) {
        venta(id: $id) {
          id
          idAnulada
          idPedido
          cliente {
            nombre
            apellido
            appUsuario
          }
          tipoFactura
          fechaFactura
          tipo_cbte
          tipo_doc
          nro_doc
          imp_iva21
          subtotal21
          concepto
          nroAnulado
          url
          pto_vta
          numeroDeFacturaAfip
          CAE
          CAEFchVto
          descuento
          recargo
          total
          mesa
          pedido {
            id
            propina
            cuentaCorriente
            detalles {
              producto {
                nombre
              }
              cantidad
              precio
            }
          }
        }
      }
    `
  );

  const [getResumenVentas] = useLazyQuery(
    gql`
      query ResumenVentasSedes($fechaInicio: String!, $fechaFin: String!, $idSede: ID) {
        ResumenVentasSedes(fechaInicio: $fechaInicio, fechaFin: $fechaFin, idSede: $idSede) {
          idSede
          destino
          fechaPedido
          total
        }
      }
    `, {
    fetchPolicy: "no-cache",
    cachePolicy: "no-cache"
  }
  );

  const [openModalTurnosHorarios, setOpenModalTurnosHorarios] = useState(false);
  const [turnosSedeData, setTurnosSedeData] = useState([]);
  const [franjasTurnoData, setFranjasTurnoData] = useState([]);
  const [selectedTurnoAdminId, setSelectedTurnoAdminId] = useState("");
  const [turnoForm, setTurnoForm] = useState(DEFAULT_TURNO_FORM);
  const [franjaForm, setFranjaForm] = useState(DEFAULT_FRANJA_FORM);
  const [configPedidosForm, setConfigPedidosForm] = useState({
    permitirPedidosDiaSiguiente: false,
    maxPedidosUsuarioPorDia: 2,
  });
  const [loadingTurnosAdmin, setLoadingTurnosAdmin] = useState(false);
  const [loadingFranjasAdmin, setLoadingFranjasAdmin] = useState(false);
  const [loadingConfigAdmin, setLoadingConfigAdmin] = useState(false);
  const [franjasByTurnoAdmin, setFranjasByTurnoAdmin] = useState({});
  const [expandedTurnoAdminId, setExpandedTurnoAdminId] = useState("");
  const [franjasDraftAdmin, setFranjasDraftAdmin] = useState([]);
  const [newFranjaDraftAdmin, setNewFranjaDraftAdmin] = useState({ ...DEFAULT_FRANJA_FORM });

  const [getTurnosSedeAdmin] = useLazyQuery(
    gql`
      query getTurnosSedeAdmin($idSede: ID!) {
        turnosSede(idSede: $idSede) {
          id
          nombre
          codigoSistema
          protegido
          bloqueadoPorUso
          bloqueadoEliminacion
          horaInicio
          horaFin
          pedidoDesdeHora
          pedidoDesdeOffsetDias
          pedidoHastaHora
          pedidoHastaOffsetDias
          orden
          activo
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [getFranjasTurnoAdmin] = useLazyQuery(
    gql`
      query getFranjasTurnoAdmin($idTurnoSede: ID!, $idSede: ID) {
        franjasTurno(idTurnoSede: $idTurnoSede, idSede: $idSede) {
          id
          idTurnoSede
          horaInicio
          horaFin
          disponible
          vencida
          sinCupo
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [getConfigPedidosSedeAdmin] = useLazyQuery(
    gql`
      query getConfigPedidosSedeAdmin($idSede: ID!) {
        configPedidosSede(idSede: $idSede) {
          id
          idSede
          permitirPedidosDiaSiguiente
          maxPedidosUsuarioPorDia
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [saveTurnoSedeAdmin, { loading: loadingSaveTurnoAdmin }] = useMutation(
    gql`
      mutation saveTurnoSedeAdmin($input: TurnoSedeInput!) {
        saveTurnoSede(input: $input) {
          id
          nombre
          codigoSistema
          protegido
          bloqueadoPorUso
          bloqueadoEliminacion
          horaInicio
          horaFin
          pedidoDesdeHora
          pedidoDesdeOffsetDias
          pedidoHastaHora
          pedidoHastaOffsetDias
          orden
          activo
        }
      }
    `
  );

  const [deleteTurnoSedeAdmin, { loading: loadingDeleteTurnoAdmin }] = useMutation(
    gql`
      mutation deleteTurnoSedeAdmin($id: ID!) {
        deleteTurnoSede(id: $id)
      }
    `
  );

  const [saveFranjaTurnoAdmin, { loading: loadingSaveFranjaAdmin }] = useMutation(
    gql`
      mutation saveFranjaTurnoAdmin($input: FranjaTurnoInput!) {
        saveFranjaTurno(input: $input) {
          id
          idTurnoSede
          horaInicio
          horaFin
        }
      }
    `
  );

  const [deleteFranjaTurnoAdmin, { loading: loadingDeleteFranjaAdmin }] = useMutation(
    gql`
      mutation deleteFranjaTurnoAdmin($id: ID!) {
        deleteFranjaTurno(id: $id)
      }
    `
  );

  const [saveConfigPedidosSedeAdmin, { loading: loadingSaveConfigAdmin }] = useMutation(
    gql`
      mutation saveConfigPedidosSedeAdmin($input: ConfigPedidosSedeInput!) {
        saveConfigPedidosSede(input: $input) {
          id
          idSede
          permitirPedidosDiaSiguiente
          maxPedidosUsuarioPorDia
        }
      }
    `
  );

  const loadTurnosSedeAdmin = async (idSeleccionado = null) => {
    if (!user?.sede?.id) return [];
    setLoadingTurnosAdmin(true);
    try {
      const { data: turnosData } = await getTurnosSedeAdmin({
        variables: {
          idSede: user.sede.id,
        },
      });
      const listado = turnosData?.turnosSede || [];
      setTurnosSedeData(listado);

      if (listado.length > 0) {
        const resultados = await Promise.all(
          listado.map(async (turno) => {
            try {
              const { data: franjasData } = await getFranjasTurnoAdmin({
                variables: {
                  idTurnoSede: turno.id,
                  idSede: user.sede.id,
                },
              });
              return [String(turno.id), franjasData?.franjasTurno || []];
            } catch {
              return [String(turno.id), []];
            }
          })
        );
        setFranjasByTurnoAdmin(Object.fromEntries(resultados));
      } else {
        setFranjasByTurnoAdmin({});
      }

      const idActual = String(idSeleccionado || selectedTurnoAdminId || "");
      const turnoExiste = listado.some((item) => String(item.id) === idActual);
      const siguienteId = turnoExiste ? idActual : String(listado[0]?.id || "");
      setSelectedTurnoAdminId(siguienteId);
      return listado;
    } catch (error) {
      handleSnackbar("Error al cargar turnos por sede", "error");
      return [];
    } finally {
      setLoadingTurnosAdmin(false);
    }
  };

  const loadFranjasTurnoAdmin = async (idTurno) => {
    if (!idTurno || !user?.sede?.id) {
      setFranjasTurnoData([]);
      return;
    }
    setLoadingFranjasAdmin(true);
    try {
      const { data: franjasData } = await getFranjasTurnoAdmin({
        variables: {
          idTurnoSede: idTurno,
          idSede: user.sede.id,
        },
      });
      const listadoFranjas = franjasData?.franjasTurno || [];
      setFranjasTurnoData(listadoFranjas);
      setFranjasByTurnoAdmin((prev) => ({
        ...prev,
        [String(idTurno)]: listadoFranjas,
      }));
    } catch (error) {
      handleSnackbar("Error al cargar franjas del turno", "error");
    } finally {
      setLoadingFranjasAdmin(false);
    }
  };

  const loadConfigPedidosAdmin = async () => {
    if (!user?.sede?.id) return;
    setLoadingConfigAdmin(true);
    try {
      const { data: configData } = await getConfigPedidosSedeAdmin({
        variables: {
          idSede: user.sede.id,
        },
      });
      const config = configData?.configPedidosSede;
      setConfigPedidosForm({
        permitirPedidosDiaSiguiente: Boolean(config?.permitirPedidosDiaSiguiente),
        maxPedidosUsuarioPorDia: Number(config?.maxPedidosUsuarioPorDia || 2),
      });
    } catch (error) {
      handleSnackbar("Error al cargar configuracion de pedidos", "error");
    } finally {
      setLoadingConfigAdmin(false);
    }
  };

  const obtenerResumenVentas = async () => {
    setLoadingResumenVentas(true);
    try {
      const { data } = await getResumenVentas({
        variables: {
          fechaInicio: filtrosResumenVentas.fechaInicio,
          fechaFin: filtrosResumenVentas.fechaFin,
          idSede: filtrosResumenVentas.idSede
        }
      });
      setResumenVentasData(data?.ResumenVentasSedes || []);
    } catch (error) {
      handleSnackbar("Error al obtener el resumen de ventas", "error");
      console.error(error);
    } finally {
      setLoadingResumenVentas(false);
    }
  };

  const { loading, data, refetch } = useQuery(
    gql`
      query getPedidos(
        $filter: JSON
        $search: String
        $page: Int
        $order: Order
        $facturado: String
      ) {
        paginationInfo {
          pages
          total
        }
        pedidos(
          filter: $filter
          search: $search
          pagination: { page: $page, limit: 10 }
          order: $order
          facturado: $facturado
        ) {
          id
          estado
          total
          pagoSplitCC
          createdAt
          updatedAt
          fechaPedido
          bandejas
          bandejasDevueltas
          horaReserva
          turnoReserva
          paraRetirar
          cuentaCorriente
          destino
          cliente {
            nombre
            apellido
            appUsuario
          }
          detalles {
            id
            numeroComanda
            cantidad
            precio
            comentario
            producto {
              nombre
              seccion
              id
            }
            ingredientesQuitados {
              id
            }
            estado
            horarioEntrega
          }
          mesa {
            numero
          }
          venta {
            id
            tipoFactura
            numeroDeFacturaAfip
            idAnulada
            CAE
            idVoucher
            voucher {
              nombreVoucher
              grupo {
                nombre
              }
            }
            pagos {
              id
              totalPago
              estado
              comprobanteFile
              medioDePago {
                nombre
              }
            }
          }
          mozo {
            nombre
            apellido
          }
        }
      }
    `,
    {
      variables: {
        filter: filtros?.fechaPedido
          ? {
            idSede: user?.sede?.id,
            estado: filtros?.estado || [
              "RESERVADO",
              "EN PREPARACIÓN",
              "CERRADO",
              "ABIERTO",
              "PAGO EN REVISIÓN",
              "PENDIENTE DE PAGO",
              "ANULADO",
            ],
            _or: [
              {
                fechaPedido: filtros?.fechaPedido,
              },
              {
                createdAt: filtros?.fechaPedido
                  ? {
                    _between: [
                      `${dayjs(filtros.fechaPedido).startOf("day")}`,
                      `${dayjs(filtros.fechaPedido).endOf("day")}`,
                    ],
                  }
                  : undefined,
              },
            ],
          }
          : {
            idSede: user?.sede?.id,
            estado: filtros?.estado || [
              "RESERVADO",
              "EN PREPARACIÓN",
              "CERRADO",
              "ABIERTO",
              "PAGO EN REVISIÓN",
              "PENDIENTE DE PAGO",
              "ANULADO",
            ],
            turnoReserva: {
              _ne: null,
            },
          },
        search: search || null,
        page: page,
        order: { field: orderBy.campo, order: orderBy.orden },
        facturado: filtros?.facturado,
      },
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [openModalDetalles, setOpenModalDetalles] = useState(false);
  const handleOpenModalDetalles = (item) => {
    setSelectedPedido(item);
    setOpenModalDetalles(true);
  };
  const handleCloseModalDetalles = () => {
    setSelectedPedido();
    setOpenModalDetalles(false);
  };

  const [openModalComprobantes, setOpenModalComprobantes] = useState(false);
  const handleOpenModalComprobantes = () => {
    setOpenModalComprobantes(true);
  };
  const handleCloseModalComprobantes = () => {
    setOpenModalComprobantes(false);
  };

  const [openModalExcel, setOpenModalExcel] = useState(false);
  const handleOpenModalExcel = () => {
    setOpenModalExcel(true);
  };
  const handleCloseModalExcel = () => {
    setOpenModalExcel(false);
  };

  const puedeAnularPedido = (item) =>
    !["CERRADO", "ANULADO"].includes(
      String(item?.estado || "").trim().toUpperCase()
    );

  const ejecutarAnulacionPedido = async (item, preview = null) => {
    if (!puedeAnularPedido(item)) {
      throw new Error("El pedido cerrado o ya anulado no puede anularse");
    }
    const tieneVentaFacturada = Boolean(preview?.tieneVentasFacturadas || item?.venta?.numeroDeFacturaAfip);

    if (tieneVentaFacturada && item?.venta?.id) {
      await deleteVenta({ variables: { id: item.venta.id } });
      return;
    }

    await anularPedido({
      variables: {
        id: item.id,
        confirmacionEliminarPagosAprobados: true,
      },
    });
  };

  const solicitarAnulacionPedido = async (item) => {
    if (!puedeAnularPedido(item)) {
      handleSnackbar("El pedido cerrado o ya anulado no puede anularse", "error");
      return;
    }
    try {
      const { data: previewData } = await getCancelacionPedidoPreview({
        variables: { id: item.id },
      });

      const preview = previewData?.cancelacionPedidoPreview || null;
      const tienePagosAprobados = Boolean(preview?.tienePagosAprobadosRelacionados);
      const tieneVentaFacturada = Boolean(preview?.tieneVentasFacturadas || item?.venta?.numeroDeFacturaAfip);

      confirmar({
        title: "Anular pedido",
        icon: tienePagosAprobados ? "warning" : "cancel",
        message: tienePagosAprobados ? (
          <PedidoCancelacionAdvertencia preview={preview} idPedido={item.id} />
        ) : tieneVentaFacturada ? (
          `¿Está seguro que desea anular el pedido ${item.id}? Se emitirá la nota de crédito correspondiente.`
        ) : (
          `¿Está seguro que desea anular el pedido ${item.id}?`
        ),
        confirmLabel: tienePagosAprobados ? "Continuar anulación" : "Confirmar",
        cancelLabel: "Cancelar",
        disableBackdropClose: tienePagosAprobados,
        func: () => {
          ejecutarAnulacionPedido(item, preview)
            .then(() => {
              handleSnackbar("Pedido anulado con éxito", "success");
              refetch();
            })
            .catch((error) => {
              handleSnackbar(
                getApolloErrorMessage(error, "Error al anular el pedido"),
                "error"
              );
            });
        },
      });
    } catch (error) {
      handleSnackbar(
        getApolloErrorMessage(error, "Error al revisar la anulaci?n del pedido"),
        "error"
      );
    }
  };

  useEffect(() => {
    if (pedidos) {
      setRows(
        getRows(pedidos, (event, item) => {
          let opciones = [
            {
              name: "Ver factura",
              icon: { icon: "receipt", color: getUproColor("uproGreen") },
              disabled: !item.venta || !item.venta?.CAE,
              onClick: () => {
                getVenta({ variables: { id: item.venta.id } }).then((res) => {
                  handlePrintFactura(res.data?.venta, user.empresa);
                });
              },
            },
            {
              name: "Ver detalles",
              icon: { icon: "visibility", color: "primary" },
              onClick: () => {
                handleOpenModalDetalles(item);
              },
            },
            {
              name: "Cambiar menú",
              icon: { icon: "edit", color: getUproColor("uproBlue") },
              disabled: item.estado !== "RESERVADO",
              onClick: () => {
                handleOpenModalCambiarMenu(item);
              },
            },
            {
              name: "Anular pedido",
              icon: { icon: "cancel", color: getUproColor("uproPink") },
              disabled: !puedeAnularPedido(item),
              onClick: () => {
                solicitarAnulacionPedido(item);
              },
            },
          ];

          if (item?.venta?.numeroDeFacturaAfip) {
            opciones.push({
              name: "Imprimir ticket",
              icon: { icon: "receipt", color: getUproColor("uproYellow") },
              disabled: !item.venta,
              onClick: async () => {
                await getVenta({ variables: { id: item.venta.id } }).then(async (res) => {
                  const data = {
                    cae: res.data.venta.CAE,
                    vencimientoCae: dayjs(res.data.venta.CAEFchVto).format("DD/MM/YYYY"),
                    url: res.data.venta.url,
                    nroDocumento: formatMoneyPunto(res.data.venta.nro_doc),
                    numeroFacturaAfip: res.data.venta.numeroDeFacturaAfip,
                    tipoFactura: res.data.venta.tipoFactura,
                    mesa: item.mesa?.numero,
                    cliente: res.data.venta.cliente
                      ? makeNiceText(
                        `${res.data.venta.cliente.nombre} ${res.data.venta.cliente.apellido}`,
                        "nombre"
                      )
                      : "Consumidor final",
                    id: res.data.venta.id,
                    url: res.data.venta.url,
                    idPedido: item.id,
                    descuento: res.data.venta.descuento.toFixed(2),
                    recargo: res.data.venta.recargo.toFixed(2),
                    total: res.data.venta.total.toFixed(2),
                  };
                  await printTicket(data, item.detalles).then((response) => {
                    if (response?.status !== 200) {
                      handleSnackbar(
                        `Error al imprimir el ticket ${response?.data?.message ? `: ${response?.data?.message}` : ""
                        }`,
                        "error"
                      );
                    }
                  });
                });
              },
            });
          }
          // if (item?.bandejas && !item.bandejasDevueltas) {
          //   opciones.push(
          //     {
          //     name: "Devolver bandejas",
          //     icon: { icon: "undo", color: getUproColor("uproBlue") },
          //     onClick: () => {
          //       confirmar({
          //         icon: "warning",
          //         title: "Devolver bandejas",
          //         message: "¿Está seguro que desea devolver las bandejas?",
          //         func: () => {
          //           devolverBandejas({
          //             variables: {
          //               id: item.id,
          //             },
          //           })
          //             .then(() => {
          //               handleSnackbar("Bandejas devueltas con éxito", "success");
          //               refetch();
          //             })
          //             .catch((err) => {
          //               handleSnackbar(err.message || "Error al devolver las bandejas", "error");
          //             });
          //         },
          //       });
          //     },
          //   });
          // }

          const pagoRevisionTransferencia =
            item?.estado === "PAGO EN REVISIÓN" ? getPagoRevisionTransferencia(item) : null;
          if (pagoRevisionTransferencia) {
            opciones.push({
              name: "Ver comprobantes",
              icon: { icon: "receipt", color: getUproColor("uproPink") },
              onClick: () => {
                setSelectedPedido(item);
                handleOpenModalComprobantes();
              },
            });
          }
          if (item?.venta?.idAnulada) {
            opciones.push({
              name: "Imprimir nota de crédito",
              icon: { icon: "print", color: getUproColor("uproBlue") },
              disabled: !item.venta.idAnulada,
              onClick: () => {
                getVenta({ variables: { id: item.venta.idAnulada } }).then((res) => {
                  printFactura(res.data.venta, user.empresa);
                });
              },
            });
          }

          // if (item.estado !== "CERRADO" ) {
          //   opciones.push({
          //     name: "Anular",
          //     icon: { icon: "close", color: getUproColor("uproPink") },
          //     disabled: !item.venta || item.venta.idAnulada || loadingDelete,
          //     onClick: () => {
          //       confirmar({
          //         icon: "warning",
          //         title: "Anular",
          //         message:
          //           "¿Está seguro que desea anular la factura?. No podrá deshacer esta acción.",
          //         func: () => {
          //           getVenta({ variables: { id: item.venta.id } }).then((res) => {
          //             deleteVenta({
          //               variables: {
          //                 id: res.data.venta.id,
          //               },
          //             })
          //               .then(() => {
          //                 refetch();
          //                 handleSnackbar("Factura anulada correctamente", "success");
          //               })
          //               .catch((err) => {
          //                 handleSnackbar(err.message || "Error al anular la factura", "error");
          //               });
          //           });
          //         },
          //       });
          //     },
          //   });
          // }
          menu({
            open: event.currentTarget,
            align: "right",
            options: opciones,
            handleClose: () => { },
          });
        })
      );
    }
  }, [pedidos]);

  useEffect(() => {
    if (data?.pedidos) {
      let pedidosFiltrados = data.pedidos;

      // Filtrar por estados personalizados
      if (filtros?.estadoPersonalizado === "PENDIENTE DE PAGO") {
        pedidosFiltrados = pedidosFiltrados.filter(
          (pedido) =>
            pedido.estado === "RESERVADO" &&
            pedido.venta?.pagos[0]?.medioDePago?.nombre === "Transferencia" &&
            pedido.venta?.pagos[0]?.estado === "Pendiente"
        );
      } else if (filtros?.estadoPersonalizado === "PAGO EN REVISIÓN") {
        pedidosFiltrados = pedidosFiltrados.filter(
          (pedido) =>
            pedido.estado === "RESERVADO" &&
            pedido.venta?.pagos[0]?.medioDePago?.nombre === "Transferencia" &&
            pedido.venta?.pagos[0]?.estado === "Revision"
        );
      }

      // Si no hay filtros personalizados, usar la paginación de la base de datos
      if (!filtros?.estadoPersonalizado) {
        setPedidos(data.pedidos); // Usar los datos directamente de la base de datos
        setPagesInfo(data.paginationInfo); // Usar la información de paginación de la base de datos
      } else {
        // Simular la paginación en el front
        const startIndex = (page - 1) * 10; // 10 registros por página
        const endIndex = startIndex + 10;
        const pedidosPaginados = pedidosFiltrados.slice(startIndex, endIndex);

        setPedidos(pedidosPaginados);
        setPagesInfo({
          pages: Math.ceil(pedidosFiltrados.length / 10), // Total de páginas
          total: pedidosFiltrados.length, // Total de registros
        });
      }
    }
  }, [data, filtros, page]);

  useEffect(() => {
    const nuevoPedido = newData?.nuevoPedido;
    if (!nuevoPedido?.id) return;

    const eventKey = `${nuevoPedido.id}:${nuevoPedido.updatedAt || nuevoPedido.createdAt || ""}`;
    if (ultimoNuevoPedidoEventRef.current === eventKey) return;
    ultimoNuevoPedidoEventRef.current = eventKey;

    refetch();

    if (nuevoPedido?.estado !== "ANULADO" && nuevoPedido?.turnoReserva !== null) {
      setTimeout(() => {
        handleSnackbar("Se ha registrado un nuevo pedido", "info");
      }, 1000);
    }
  }, [newData, refetch, handleSnackbar]);

  useEffect(() => {
    const pedidoModificado = modifiedData?.pedidoModificado;
    if (!pedidoModificado?.id) return;

    const eventKey = `${pedidoModificado.id}:${pedidoModificado.updatedAt || pedidoModificado.createdAt || ""}:${pedidoModificado.estado || ""}`;
    if (ultimoPedidoModificadoEventRef.current === eventKey) return;
    ultimoPedidoModificadoEventRef.current = eventKey;

    refetch();
  }, [modifiedData, refetch]);

  const handleOpenFacturaModal = (factura, empresa) => {
    setFacturaData(factura); // Guardar los datos de la factura
    setEmpresaData(empresa); // Guardar los datos de la empresa
    setOpenModalFactura(true); // Abrir el modal
  };

  const handleCloseFacturaModal = () => {
    setOpenModalFactura(false); // Cerrar el modal
    setFacturaData(null); // Limpiar los datos de la factura
    setEmpresaData(null); // Limpiar los datos de la empresa
  };

  const handlePrintFactura = (venta, empresa) => {
    handleOpenFacturaModal(venta, empresa);
  };

  const [openModalNuevoPedido, setOpenModalNuevoPedido] = useState(false);
  const handleOpenModalNuevoPedido = () => {
    setOpenModalNuevoPedido(true);
  };

  const handleCloseModalNuevoPedido = () => {
    setOpenModalNuevoPedido(false);
  };

  const [openModalPrecioMenu, setOpenModalPrecioMenu] = useState(false);
  const handleOpenModalPrecioMenu = () => {
    setOpenModalPrecioMenu(true);
  };

  const handleCloseModalPrecioMenu = () => {
    setOpenModalPrecioMenu(false);
  };

  const [openModalResumenVentasLocalidad, setOpenModalResumenVentasLocalidad] = useState(false);
  const [resumenVentasData, setResumenVentasData] = useState([]);
  const [loadingResumenVentas, setLoadingResumenVentas] = useState(false);
  const [filtrosResumenVentas, setFiltrosResumenVentas] = useState({
    fechaInicio: dayjs().startOf('month').format('YYYY-MM-DD'),
    fechaFin: dayjs().format('YYYY-MM-DD'),
    idSede: user?.sede?.id || null
  });

  const handleOpenModalResumenVentasLocalidad = () => {
    setOpenModalResumenVentasLocalidad(true);
    // obtenerResumenVentas();
  };

  const handleOpenModalCambiarMenu = (pedido) => {
    setSelectedPedido(pedido);
    setOpenModalCambiarMenu(true);
  };

  const handleCloseModalCambiarMenu = () => {
    setSelectedPedido(null);
    setOpenModalCambiarMenu(false);
  };

  const handleOpenModalTurnosHorarios = () => {
    setTurnoForm({ ...DEFAULT_TURNO_FORM });
    setFranjaForm({ ...DEFAULT_FRANJA_FORM });
    setExpandedTurnoAdminId("");
    setFranjasDraftAdmin([]);
    setNewFranjaDraftAdmin({ ...DEFAULT_FRANJA_FORM });
    setOpenModalTurnosHorarios(true);
  };

  const handleCloseModalTurnosHorarios = () => {
    setOpenModalTurnosHorarios(false);
    setExpandedTurnoAdminId("");
    setSelectedTurnoAdminId("");
    setTurnoForm({ ...DEFAULT_TURNO_FORM });
    setFranjaForm({ ...DEFAULT_FRANJA_FORM });
    setFranjasDraftAdmin([]);
    setNewFranjaDraftAdmin({ ...DEFAULT_FRANJA_FORM });
    setFranjasTurnoData([]);
  };

  const handleStartCreateTurnoAdmin = () => {
    if (!puedeCrearTurnosEnSede) {
      handleSnackbar("Sede 2 no tiene permitido agregar nuevos turnos.", "warning");
      return;
    }
    setExpandedTurnoAdminId(NUEVO_TURNO_ADMIN_ID);
    setSelectedTurnoAdminId("");
    setTurnoForm({
      ...DEFAULT_TURNO_FORM,
      orden: Number((turnosSedeData?.length || 0) + 1),
    });
    setFranjaForm({ ...DEFAULT_FRANJA_FORM });
    setFranjasDraftAdmin([]);
    setNewFranjaDraftAdmin({ ...DEFAULT_FRANJA_FORM });
  };

  const handleToggleTurnoAdminCard = (turno) => {
    const id = String(turno?.id || "");
    if (!id) return;

    if (String(expandedTurnoAdminId) === id) {
      setExpandedTurnoAdminId("");
      setSelectedTurnoAdminId("");
      setTurnoForm({ ...DEFAULT_TURNO_FORM });
      setFranjaForm({ ...DEFAULT_FRANJA_FORM });
      setFranjasDraftAdmin([]);
      setNewFranjaDraftAdmin({ ...DEFAULT_FRANJA_FORM });
      return;
    }

    if (id === NUEVO_TURNO_ADMIN_ID) {
      handleStartCreateTurnoAdmin();
      return;
    }

    setExpandedTurnoAdminId(id);
    setSelectedTurnoAdminId(id);
    handleEditTurnoAdmin(turno);
    setNewFranjaDraftAdmin({ ...DEFAULT_FRANJA_FORM });
  };

  const handleFranjaDraftChange = (franjaId, field, value) => {
    setFranjasDraftAdmin((prev) =>
      prev.map((item) => (String(item.id) === String(franjaId) ? { ...item, [field]: value } : item))
    );
  };

  const handleSaveFranjaDraft = async (franjaId) => {
    const franja = franjasDraftAdmin.find((item) => String(item.id) === String(franjaId));
    if (!franja) return;

    if (!franja.horaInicio || !franja.horaFin) {
      handleSnackbar("Debe completar hora inicio y hora fin de la franja", "warning");
      return;
    }
    try {
      await saveFranjaTurnoAdmin({
        variables: {
          input: {
            id: franja.id,
            idTurnoSede: selectedTurnoAdminId,
            horaInicio: franja.horaInicio,
            horaFin: franja.horaFin,
            activo: true,
          },
        },
      });
      handleSnackbar("Franja guardada correctamente", "success");
      await loadFranjasTurnoAdmin(selectedTurnoAdminId);
    } catch (error) {
      handleSnackbar(getApolloErrorMessage(error, "Error al guardar franja"), "error");
    }
  };

  const handleCreateFranjaForExpandedTurno = async () => {
    if (!selectedTurnoAdminId) {
      handleSnackbar("Debe seleccionar un turno para administrar franjas", "warning");
      return;
    }
    if (!newFranjaDraftAdmin.horaInicio || !newFranjaDraftAdmin.horaFin) {
      handleSnackbar("Debe completar hora inicio y hora fin de la franja", "warning");
      return;
    }
    try {
      await saveFranjaTurnoAdmin({
        variables: {
          input: {
            idTurnoSede: selectedTurnoAdminId,
            horaInicio: newFranjaDraftAdmin.horaInicio,
            horaFin: newFranjaDraftAdmin.horaFin,
            activo: true,
          },
        },
      });
      handleSnackbar("Franja guardada correctamente", "success");
      setNewFranjaDraftAdmin({ ...DEFAULT_FRANJA_FORM });
      await loadFranjasTurnoAdmin(selectedTurnoAdminId);
    } catch (error) {
      handleSnackbar(getApolloErrorMessage(error, "Error al guardar franja"), "error");
    }
  };

  const handleSaveTurnoAdmin = async () => {
    if (!user?.sede?.id) return;
    if (!turnoForm.nombre?.trim()) {
      handleSnackbar("Debe ingresar un nombre de turno", "warning");
      return;
    }
    if (!turnoForm.horaInicio || !turnoForm.horaFin) {
      handleSnackbar("Debe completar hora inicio y hora fin del turno", "warning");
      return;
    }
    const usaVentanaPedidos = Boolean(turnoForm.pedidoDesdeHora || turnoForm.pedidoHastaHora);
    if (usaVentanaPedidos && (!turnoForm.pedidoDesdeHora || !turnoForm.pedidoHastaHora)) {
      handleSnackbar(
        "Si configura franja de pedidos, debe completar hora inicio y hora fin",
        "warning"
      );
      return;
    }

    const cruzaMedianochePedidos =
      turnoForm.pedidoDesdeHora &&
      turnoForm.pedidoHastaHora &&
      turnoForm.pedidoDesdeHora > turnoForm.pedidoHastaHora;

    const pedidoDesdeOffsetDias = usaVentanaPedidos
      ? cruzaMedianochePedidos
        ? -1
        : 0
      : null;

    const pedidoHastaOffsetDias = usaVentanaPedidos ? 0 : null;

    try {
      const { data: turnoResp } = await saveTurnoSedeAdmin({
        variables: {
          input: {
            ...(turnoForm.id ? { id: turnoForm.id } : {}),
            idSede: user.sede.id,
            nombre: turnoForm.nombre.trim(),
            horaInicio: turnoForm.horaInicio,
            horaFin: turnoForm.horaFin,
            ...(usaVentanaPedidos
              ? {
                pedidoDesdeHora: turnoForm.pedidoDesdeHora,
                pedidoDesdeOffsetDias,
                pedidoHastaHora: turnoForm.pedidoHastaHora,
                pedidoHastaOffsetDias,
              }
              : {
                pedidoDesdeHora: null,
                pedidoDesdeOffsetDias: null,
                pedidoHastaHora: null,
                pedidoHastaOffsetDias: null,
              }),
            orden: Number(turnoForm.orden || 1),
            activo: true,
          },
        },
      });
      const idNuevoTurno = turnoResp?.saveTurnoSede?.id;
      handleSnackbar("Turno guardado correctamente", "success");
      const listadoActualizado = await loadTurnosSedeAdmin(idNuevoTurno || turnoForm.id);
      const idObjetivo = String(idNuevoTurno || turnoForm.id || "");
      const turnoActualizado =
        listadoActualizado.find((item) => String(item.id) === idObjetivo) || listadoActualizado[0];
      if (turnoActualizado) {
        setExpandedTurnoAdminId(String(turnoActualizado.id));
        setSelectedTurnoAdminId(String(turnoActualizado.id));
        handleEditTurnoAdmin(turnoActualizado);
        await loadFranjasTurnoAdmin(turnoActualizado.id);
      }
    } catch (error) {
      handleSnackbar(getApolloErrorMessage(error, "Error al guardar turno"), "error");
    }
  };

  const handleEditTurnoAdmin = (turno) => {
    const pedidoDesdeOffsetDias = Number(turno?.pedidoDesdeOffsetDias || 0);
    const pedidoHastaOffsetDias = Number(turno?.pedidoHastaOffsetDias || 0);

    setTurnoForm({
      id: turno?.id || "",
      nombre: turno?.nombre || "",
      codigoSistema: turno?.codigoSistema || null,
      protegido: Boolean(turno?.protegido),
      bloqueadoPorUso: Boolean(turno?.bloqueadoPorUso),
      bloqueadoEliminacion: Boolean(turno?.bloqueadoEliminacion),
      horaInicio: toTimeInput(turno?.horaInicio),
      horaFin: toTimeInput(turno?.horaFin),
      pedidoDesdeHora: toTimeInput(turno?.pedidoDesdeHora),
      pedidoDesdeOffsetDias,
      pedidoHastaHora: toTimeInput(turno?.pedidoHastaHora),
      pedidoHastaOffsetDias,
      pedidoRelacionDia: pedidoDesdeOffsetDias < 0 ? "dia_anterior" : "mismo_dia",
      orden: Number(turno?.orden || 1),
    });
  };

  const handleDeleteTurnoAdmin = (turno) => {
    if (isTurnoProtegidoAdmin(turno)) {
      if (turno?.bloqueadoEliminacion) {
        handleSnackbar(
          "Este turno tiene menus activos o futuros asignados y no se puede eliminar.",
          "warning"
        );
      } else {
        handleSnackbar("Este turno de sistema no se puede eliminar.", "warning");
      }
      return;
    }
    confirmar({
      title: "Eliminar turno",
      message: `Se eliminara el turno "${formatTurnoNombreAdmin(turno?.nombre)}". Desea continuar?`,
      func: async () => {
        try {
          await deleteTurnoSedeAdmin({
            variables: {
              id: turno.id,
            },
          });
          handleSnackbar("Turno eliminado correctamente", "success");
          if (String(selectedTurnoAdminId) === String(turno.id)) {
            setSelectedTurnoAdminId("");
            setFranjaForm({ ...DEFAULT_FRANJA_FORM });
          }
          await loadTurnosSedeAdmin();
        } catch (error) {
          handleSnackbar(getApolloErrorMessage(error, "Error al eliminar turno"), "error");
        }
      },
    });
  };

  const handleSaveFranjaAdmin = async () => {
    if (!selectedTurnoAdminId) {
      handleSnackbar("Debe seleccionar un turno para administrar franjas", "warning");
      return;
    }
    if (!franjaForm.horaInicio || !franjaForm.horaFin) {
      handleSnackbar("Debe completar hora inicio y hora fin de la franja", "warning");
      return;
    }
    try {
      await saveFranjaTurnoAdmin({
        variables: {
          input: {
            ...(franjaForm.id ? { id: franjaForm.id } : {}),
            idTurnoSede: selectedTurnoAdminId,
            horaInicio: franjaForm.horaInicio,
            horaFin: franjaForm.horaFin,
            activo: true,
          },
        },
      });
      handleSnackbar("Franja guardada correctamente", "success");
      setFranjaForm({ ...DEFAULT_FRANJA_FORM });
      await loadFranjasTurnoAdmin(selectedTurnoAdminId);
    } catch (error) {
      handleSnackbar(getApolloErrorMessage(error, "Error al guardar franja"), "error");
    }
  };

  const handleEditFranjaAdmin = (franja) => {
    setFranjaForm({
      id: franja?.id || "",
      horaInicio: toTimeInput(franja?.horaInicio),
      horaFin: toTimeInput(franja?.horaFin),
    });
  };

  const handleDeleteFranjaAdmin = (franja) => {
    confirmar({
      title: "Eliminar franja",
      message: `Se eliminara la franja ${toTimeInput(franja?.horaInicio)} - ${toTimeInput(
        franja?.horaFin
      )}. Desea continuar?`,
      func: async () => {
        try {
          await deleteFranjaTurnoAdmin({
            variables: {
              id: franja.id,
            },
          });
          handleSnackbar("Franja eliminada correctamente", "success");
          await loadFranjasTurnoAdmin(selectedTurnoAdminId);
        } catch (error) {
          handleSnackbar(getApolloErrorMessage(error, "Error al eliminar franja"), "error");
        }
      },
    });
  };

  const handleSaveConfigPedidosAdmin = async () => {
    if (!user?.sede?.id) return;
    if (Number(configPedidosForm.maxPedidosUsuarioPorDia) <= 0) {
      handleSnackbar("El maximo de pedidos por dia debe ser mayor a cero", "warning");
      return;
    }

    try {
      await saveConfigPedidosSedeAdmin({
        variables: {
          input: {
            idSede: user.sede.id,
            permitirPedidosDiaSiguiente: Boolean(configPedidosForm.permitirPedidosDiaSiguiente),
            maxPedidosUsuarioPorDia: Number(configPedidosForm.maxPedidosUsuarioPorDia),
          },
        },
      });
      handleSnackbar("Configuracion guardada correctamente", "success");
      await loadConfigPedidosAdmin();
    } catch (error) {
      handleSnackbar(getApolloErrorMessage(error, "Error al guardar configuracion"), "error");
    }
  };

  useEffect(() => {
    if (!openModalTurnosHorarios) return;
    const init = async () => {
      await Promise.all([loadTurnosSedeAdmin(), loadConfigPedidosAdmin()]);
    };
    init();
  }, [openModalTurnosHorarios, user?.sede?.id]);

  useEffect(() => {
    if (!openModalTurnosHorarios) return;
    if (!selectedTurnoAdminId) {
      setFranjasTurnoData([]);
      setFranjaForm({ ...DEFAULT_FRANJA_FORM });
      setFranjasDraftAdmin([]);
      setNewFranjaDraftAdmin({ ...DEFAULT_FRANJA_FORM });
      return;
    }
    setFranjaForm({ ...DEFAULT_FRANJA_FORM });
    setNewFranjaDraftAdmin({ ...DEFAULT_FRANJA_FORM });
    loadFranjasTurnoAdmin(selectedTurnoAdminId);
  }, [openModalTurnosHorarios, selectedTurnoAdminId]);

  useEffect(() => {
    const cruzaMedianochePedidos =
      turnoForm.pedidoDesdeHora &&
      turnoForm.pedidoHastaHora &&
      turnoForm.pedidoDesdeHora > turnoForm.pedidoHastaHora;

    if (!cruzaMedianochePedidos) return;
    if (turnoForm.pedidoRelacionDia === "dia_anterior") return;

    setTurnoForm((prev) => ({
      ...prev,
      pedidoRelacionDia: "dia_anterior",
      pedidoDesdeOffsetDias: -1,
      pedidoHastaOffsetDias: 0,
    }));
  }, [turnoForm.pedidoDesdeHora, turnoForm.pedidoHastaHora, turnoForm.pedidoRelacionDia]);

  useEffect(() => {
    setFranjasDraftAdmin(
      (franjasTurnoData || []).map((franja) => ({
        id: String(franja.id),
        horaInicio: toTimeInput(franja.horaInicio),
        horaFin: toTimeInput(franja.horaFin),
      }))
    );
  }, [franjasTurnoData]);

  useEffect(() => {
    if (openModalResumenVentasLocalidad) {
      setResumenVentasData([]);
      setLoadingResumenVentas(false);
      setFiltrosResumenVentas({
        fechaInicio: dayjs().startOf('month').format('YYYY-MM-DD'),
        fechaFin: dayjs().format('YYYY-MM-DD'),
        idSede: user?.sede?.id || null
      });
    }
  }, [openModalResumenVentasLocalidad]);

  const guardandoTurnosAdmin = loadingSaveTurnoAdmin || loadingDeleteTurnoAdmin;
  const guardandoFranjasAdmin = loadingSaveFranjaAdmin || loadingDeleteFranjaAdmin;
  const sedeLabelAdmin =
    user?.sede?.nombre || (user?.sede?.id ? `Sede ${user.sede.id}` : "Sede");
  const formatTurnoNombreAdmin = (nombre = "") =>
    user?.tipoUser === "Administrador" ? `${nombre} (sede ${sedeLabelAdmin})` : nombre;
  const totalTurnosOrdenAdmin = turnosSedeData.length;
  const creandoTurnoAdmin = String(expandedTurnoAdminId) === NUEVO_TURNO_ADMIN_ID;
  const esCreacionTurnoAdmin = !turnoForm.id;
  const cantidadOpcionesOrdenAdmin = esCreacionTurnoAdmin
    ? totalTurnosOrdenAdmin + 1
    : totalTurnosOrdenAdmin;
  const opcionesOrdenAdmin = Array.from(
    { length: Math.max(cantidadOpcionesOrdenAdmin, 1) },
    (_, idx) => idx + 1
  );
  const mostrarSelectOrdenAdmin = opcionesOrdenAdmin.length > 1;
  const turnosRenderAdmin = creandoTurnoAdmin
    ? [...turnosSedeData, { id: NUEVO_TURNO_ADMIN_ID, nombre: "Nuevo turno" }]
    : turnosSedeData;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox
                  display={{
                    xs: "flex-row",
                    sm: "flex",
                  }}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                    <SoftTypography variant="h6">{`Pedidos ${filtros?.fechaPedido ? "de hoy" : ""
                      }`}</SoftTypography>
                  </SoftBox>
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="end"
                    mt={{
                      xs: 2,
                      sm: 0,
                    }}
                    gap={2}
                  >
                    <SoftBox
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "auto",
                        },
                      }}
                    >
                      <SoftInput
                        placeholder="Escribe aquí..."
                        icon={{ component: "search", direction: "left" }}
                        onChange={handleSearch}
                      />
                    </SoftBox>
                    <SoftBox>
                      <Tooltip title="Seleccionar fecha" placement="top">
                        <SoftInput
                          type="date"
                          value={filtros?.fechaPedido || ""}
                          onChange={(event) => {
                            const selectedDate = event.target.value;
                            let filterNew = filtros ? { ...filtros } : {};
                            if (selectedDate) {
                              filterNew.fechaPedido = selectedDate;
                            } else {
                              delete filterNew.fechaPedido;
                            }
                            setFiltros(filterNew);
                            setPage(1);
                          }}
                          sx={{
                            width: "auto",
                            minWidth: "150px",
                          }}
                        />
                      </Tooltip>
                    </SoftBox>
                    <SoftBox>
                      <Tooltip
                        title={
                          filtros?.fechaPedido === dayjs().format("YYYY-MM-DD")
                            ? "Ver pedidos de todos los días"
                            : "Ver solo pedidos de hoy"
                        }
                        placement="top"
                      >
                        <SoftButton
                          circular
                          iconOnly
                          color={
                            filtros?.fechaPedido === dayjs().format("YYYY-MM-DD")
                              ? "uproYellow"
                              : "uproGreen"
                          }
                          onClick={() => {
                            let filterNew = filtros ? { ...filtros } : {};
                            if (filtros?.fechaPedido) {
                              delete filterNew.fechaPedido;
                            } else {
                              filterNew.fechaPedido = dayjs().format("YYYY-MM-DD");
                            }
                            setFiltros(filterNew);
                            setPage(1);
                          }}
                          sx={{
                            transition: "all 0.5s",
                          }}
                        >
                          <Icon
                            sx={{
                              fontSize: "16px !important",
                            }}
                          >
                            {filtros?.fechaPedido === dayjs().format("YYYY-MM-DD")
                              ? "today"
                              : "date_range"}
                          </Icon>
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                    {user?.sede?.id == 1 && (<SoftBox>
                      <Tooltip title="Cambiar precio del menu universitario" placement="top">
                        <SoftButton
                          color="uproBlue"
                          circular
                          iconOnly
                          onClick={() => {
                            handleOpenModalPrecioMenu();
                          }}
                          sx={{
                            display: { xs: "none", sm: "flex" },
                          }}
                        >
                          <Icon>price_change</Icon>
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>)}
                    <SoftBox>
                      <Tooltip title="Resumen de ventas por localidad" placement="top">
                        <SoftButton
                          color="uproPink"
                          circular
                          iconOnly
                          onClick={() => {
                            handleOpenModalResumenVentasLocalidad();
                          }}
                          sx={{
                            display: { xs: "none", sm: "flex" },
                          }}
                        >
                          <Icon>article</Icon>
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                    <SoftBox>
                      <Tooltip title="Administrar turnos y horarios" placement="top">
                        <SoftButton
                          color="dark"
                          circular
                          iconOnly
                          onClick={handleOpenModalTurnosHorarios}
                          sx={{
                            display: { xs: "none", sm: "flex" },
                          }}
                        >
                          <Icon>schedule</Icon>
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                    {/* {user?.sede?.id == 1 && ( */}
                    <Tooltip
                      title="Menú del día"
                      placement="top"
                      onClick={() => {
                        setModalMenu(true);
                      }}
                    >
                      <SoftButton
                        color="uproGreen"
                        circular
                        sx={{
                          display: { xs: "none", sm: "flex" },
                        }}
                      >
                        <Icon>menu_book</Icon>
                        &nbsp;Menú del día
                      </SoftButton>
                    </Tooltip>
                    {/* )} */}
                    <SoftBox
                      display={{
                        xs: "none",
                        sm: "flex",
                      }}
                      alignItems="center"
                    >
                      <Tooltip title="Filtrar por estado" placement="top">
                        <SoftButton
                          circular
                          variant="color"
                          color={
                            Array.isArray(filtros?.estado) &&
                              filtros?.estado?.length === 2 &&
                              filtros?.estado?.includes("PAGO EN REVISIÓN") &&
                              filtros?.estado?.includes("RESERVADO")
                              ? getUproColor("uproGreen")
                              : filtros?.estado === "RESERVADO"
                                ? getUproColor("uproYellow")
                                : filtros?.estado === "EN PREPARACIÓN"
                                  ? getUproColor("uproPink")
                                  : filtros?.estado === "CERRADO" && !filtros?.facturado
                                    ? getUproColor("uproGreen")
                                    : filtros?.estado === "ABIERTO"
                                      ? getUproColor("uproPink")
                                      : filtros?.estado === "CERRADO" && filtros?.facturado
                                        ? getUproColor("error")
                                        : filtros?.estado === "PENDIENTE DE PAGO"
                                          ? getUproColor("error")
                                          : filtros?.estado === "PAGO EN REVISIÓN"
                                            ? getUproColor("uproBlue")
                                            : filtros?.estado === "ANULADO"
                                              ? getUproColor("error")
                                              : getUproColor("uproBlue")
                          }
                          sx={{
                            transition: "all 0.5s",
                          }}
                          onClick={(event) => {
                            setExpandFiltroEstado(true);
                            menu({
                              open: event.currentTarget,
                              align: "right",
                              options: [
                                {
                                  name: "Todos",
                                  icon: { icon: "filter_alt", color: getUproColor("uproBlue") },
                                  onClick: () => {
                                    let filterNew = filtros ? { ...filtros, facturado: null } : {};
                                    delete filterNew.estado;
                                    setFiltros(filterNew);
                                    setPage(1);
                                  },
                                },
                                {
                                  name: "Pedidos activos",
                                  icon: { icon: "play_circle", color: getUproColor("uproGreen") },
                                  onClick: () => {
                                    setFiltros({
                                      ...filtros,
                                      estado: ["PAGO EN REVISIÓN", "RESERVADO"],
                                      facturado: null,
                                    });
                                    setPage(1);
                                  },
                                },
                                {
                                  name: "Reservado",
                                  icon: { icon: "note_alt", color: getUproColor("uproYellow") },
                                  onClick: () => {
                                    setFiltros({
                                      ...filtros,
                                      estado: "RESERVADO",
                                      facturado: null,
                                    });
                                    setPage(1);
                                  },
                                },
                                {
                                  name: "En preparación",
                                  icon: { icon: "assignment", color: getUproColor("uproPink") },
                                  onClick: () => {
                                    setFiltros({
                                      ...filtros,
                                      estado: "EN PREPARACIÓN",
                                      facturado: null,
                                    });
                                    setPage(1);
                                  },
                                },
                                {
                                  name: "Cerrado",
                                  icon: {
                                    icon: "assignment_turned_in",
                                    color: getUproColor("uproGreen"),
                                  },
                                  onClick: () => {
                                    setFiltros({ ...filtros, estado: "CERRADO", facturado: null });
                                    setPage(1);
                                  },
                                },
                                {
                                  name: "Abierto",
                                  icon: {
                                    icon: "assignment_late",
                                    color: getUproColor("uproPink"),
                                  },
                                  onClick: () => {
                                    setFiltros({ ...filtros, estado: "ABIERTO", facturado: null });
                                    setPage(1);
                                  },
                                },
                                {
                                  name: "Pendiente de pago",
                                  icon: { icon: "hourglass_empty", color: getUproColor("error") },
                                  onClick: () => {
                                    setFiltros({
                                      ...filtros,
                                      estado: "PENDIENTE DE PAGO",
                                      facturado: null,
                                    });
                                    setPage(1);
                                  },
                                },
                                {
                                  name: "Pago en revisión",
                                  icon: { icon: "hourglass_top", color: getUproColor("uproBlue") },
                                  onClick: () => {
                                    setFiltros({
                                      ...filtros,
                                      estado: "PAGO EN REVISIÓN",
                                      facturado: null,
                                    });
                                    setPage(1);
                                  },
                                },
                                {
                                  name: "Sin facturar",
                                  icon: { icon: "error", color: getUproColor("error") },
                                  onClick: () => {
                                    setFiltros({
                                      ...filtros,
                                      estado: "CERRADO",
                                      facturado: "Sin facturar",
                                    });
                                    setPage(1);
                                  },
                                },
                                {
                                  name: "Anulado",
                                  icon: { icon: "cancel", color: getUproColor("error") },
                                  onClick: () => {
                                    setFiltros({
                                      ...filtros,
                                      estado: "ANULADO",
                                      facturado: null,
                                    });
                                    setPage(1);
                                  },
                                },
                              ],
                              handleClose: () => { },
                              onClose: () => {
                                setExpandFiltroEstado(false);
                              },
                            });
                          }}
                        >
                          <Icon
                            sx={{
                              fontSize: "16px !important",
                              verticalAlign: "middle",
                              mr: 0.5,
                            }}
                          >
                            {Array.isArray(filtros?.estado) &&
                              filtros?.estado?.length === 2 &&
                              filtros?.estado?.includes("PAGO EN REVISIÓN") &&
                              filtros?.estado?.includes("RESERVADO")
                              ? "play_circle"
                              : filtros?.estado === "RESERVADO"
                                ? "note_alt"
                                : filtros?.estado === "EN PREPARACIÓN"
                                  ? "assignment"
                                  : filtros?.estado === "CERRADO" && !filtros?.facturado
                                    ? "assignment_turned_in"
                                    : filtros?.estado === "ABIERTO"
                                      ? "assignment_late"
                                      : filtros?.estado === "CERRADO" && filtros?.facturado
                                        ? "error"
                                        : filtros?.estado === "PENDIENTE DE PAGO"
                                          ? "hourglass_empty"
                                          : filtros?.estado === "PAGO EN REVISIÓN"
                                            ? "hourglass_top"
                                            : filtros?.estado === "ANULADO"
                                              ? "cancel"
                                              : "filter_alt"}
                          </Icon>
                          &nbsp;
                          {Array.isArray(filtros?.estado) &&
                            filtros?.estado?.length === 2 &&
                            filtros?.estado?.includes("PAGO EN REVISIÓN") &&
                            filtros?.estado?.includes("RESERVADO")
                            ? "Pedidos activos"
                            : filtros?.estado === "CERRADO" && filtros?.facturado
                              ? "Sin facturar"
                              : filtros?.estado || "Todos"}
                          &nbsp;
                          <Icon
                            sx={{
                              rotate: expandFiltroEstado ? "180deg" : "0deg",
                              transition: "all 0.5s",
                              fontSize: "16px !important",
                              verticalAlign: "middle",
                              ml: 0.5,
                            }}
                          >
                            expand_more
                          </Icon>
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                    <SoftBox
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "auto",
                        },
                      }}
                    >
                      <SoftButton
                        color="primary"
                        onClick={() => {
                          setOpenModalNuevoPedido(true);
                        }}
                        circular
                      >
                        <Icon>add</Icon>
                        &nbsp;Nuevo pedido
                      </SoftButton>
                    </SoftBox>
                    <SoftBox>
                      <Tooltip title="Descargar excel" placement="top">
                        <SoftButton
                          circular
                          onClick={() => {
                            handleOpenModalExcel();
                          }}
                          iconOnly
                          color="uproYellow"
                        >
                          <Icon>download</Icon>
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              </Grid>
            </Grid>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <Loading />
              ) : rows?.length > 0 ? (
                <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={pedidos.length} // Número de registros en la página actual
              total={pagesInfo?.total || 0} // Total de registros después del filtrado
              pages={pagesInfo?.pages || 0} // Total de páginas después del filtrado
              page={page}
              setPage={setPage} // Cambiar la página actual
              noCard
            />
          </Card>
          <ModalBandejas
            open={openModalBandejas}
            initial={selectedPedido?.bandejas}
            handleClose={() => {
              setOpenModalBandejas(false);
              setSelectedPedido();
            }}
            onConfirm={(value) => {
              setOpenModalBandejas(false);
              finalizarPedido({
                variables: {
                  id: selectedPedido.id,
                  bandejas: value,
                },
              })
                .then(() => {
                  handleSnackbar("Pedido finalizado con éxito", "success");
                  refetch();
                  setSelectedPedido();
                })
                .catch((err) => {
                  handleSnackbar(err.message || "Error al finalizar el pedido", "error");
                  setSelectedPedido();
                });
            }}
          />
          <ModalMenuDelDia
            open={modalMenu}
            handleClose={() => {
              setModalMenu(false);
            }}
          />
          <ModalDetalles
            open={openModalDetalles}
            handleClose={handleCloseModalDetalles}
            item={selectedPedido}
          />
          <ModalComprobantes
            open={openModalComprobantes}
            handleClose={handleCloseModalComprobantes}
            data={
              getPagoRevisionTransferencia(selectedPedido) ||
              selectedPedido?.venta?.pagos?.[0] ||
              null
            }
            idPedido={selectedPedido?.id}
            refetch={refetch}
          />
          <ModalNuevoPedido
            open={openModalNuevoPedido}
            handleClose={handleCloseModalNuevoPedido}
            refetch={refetch}
          />
          <ModalPrecioMenu open={openModalPrecioMenu} handleClose={handleCloseModalPrecioMenu} />
          <ModalCambiarMenu
            open={openModalCambiarMenu}
            handleClose={handleCloseModalCambiarMenu}
            refetch={refetch}
            pedido={selectedPedido}
          />
          <Modal
            open={openModalTurnosHorarios}
            onClose={handleCloseModalTurnosHorarios}
            closeAfterTransition
            aria-labelledby="modal-turnos-horarios-title"
          >
            <Fade in={openModalTurnosHorarios}>
              <Card
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "95%",
                  maxWidth: "1100px",
                  maxHeight: "92vh",
                  overflow: "auto",
                  p: 3,
                }}
              >
                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <SoftBox display="flex" alignItems="center" gap={1}>
                    <Icon color="dark">schedule</Icon>
                    <SoftTypography id="modal-turnos-horarios-title" variant="h6">
                      Administrar turnos y horarios
                    </SoftTypography>
                  </SoftBox>
                  <SoftButton
                    variant="gradient"
                    color="secondary"
                    onClick={handleCloseModalTurnosHorarios}
                    circular
                    iconOnly
                  >
                    <Icon>close</Icon>
                  </SoftButton>
                </SoftBox>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={10}>
                    <Card sx={{ p: 2 }}>
                      <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <SoftTypography variant="button" fontWeight="bold">
                          Turnos de la sede
                        </SoftTypography>
                        <SoftButton
                          color="primary"
                          size="small"
                          onClick={handleStartCreateTurnoAdmin}
                          disabled={!puedeCrearTurnosEnSede}
                        >
                          <Icon sx={{ fontSize: "16px !important" }}>add</Icon>&nbsp;Agregar
                        </SoftButton>
                      </SoftBox>

                      {loadingTurnosAdmin ? (
                        <SoftBox py={4} display="flex" alignItems="center" justifyContent="center">
                          <CircularProgress size={24} />
                        </SoftBox>
                      ) : turnosRenderAdmin.length === 0 ? (
                        <SoftBox>
                          <SoftTypography variant="caption" color="secondary" display="block" mb={1}>
                            No hay turnos cargados para esta sede. Presione Agregar para crear el primer turno.
                          </SoftTypography>
                        </SoftBox>
                      ) : (
                        turnosRenderAdmin.map((turno) => {
                          const isExpanded = String(expandedTurnoAdminId) === String(turno.id);
                          const isDraftCard = String(turno.id) === NUEVO_TURNO_ADMIN_ID;
                          const isTurnoSistema =
                            !isDraftCard &&
                            (Boolean(turno?.protegido) ||
                              ["MEDIODIA", "NOCHE"].includes(String(turno?.codigoSistema || "")));
                          const isTurnoProtegido = !isDraftCard && isTurnoProtegidoAdmin(turno);
                          const franjasLectura = isDraftCard
                            ? []
                            : franjasByTurnoAdmin[String(turno.id)] || [];
                          const cruzaMedianocheVentanaPedidos =
                            Boolean(turnoForm.pedidoDesdeHora) &&
                            Boolean(turnoForm.pedidoHastaHora) &&
                            turnoForm.pedidoDesdeHora > turnoForm.pedidoHastaHora;
                          return (
                            <SoftBox
                              key={turno.id}
                              p={1.5}
                              mb={1.5}
                              borderRadius="lg"
                              sx={{ border: "1px solid #d6d6d6", backgroundColor: "#fff" }}
                            >
                              <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                <SoftBox display="flex" alignItems="center" gap={1}>
                                  <SoftButton
                                    iconOnly
                                    circular
                                    color="info"
                                    size="small"
                                    onClick={() => handleToggleTurnoAdminCard(turno)}
                                  >
                                    <Icon sx={{ fontSize: "16px !important" }}>
                                      {isExpanded ? "expand_more" : "chevron_right"}
                                    </Icon>
                                  </SoftButton>
                                  <SoftTypography variant="button" fontWeight="bold">
                                    {formatTurnoNombreAdmin(turno.nombre)}
                                    {isTurnoSistema
                                      ? " · Sistema"
                                      : turno?.bloqueadoPorUso
                                        ? " · En uso"
                                        : ""}
                                    {!isExpanded && !isDraftCard && turno?.horaInicio && turno?.horaFin
                                      ? ` · ${toTimeInput(turno.horaInicio)} - ${toTimeInput(turno.horaFin)}`
                                      : ""}
                                  </SoftTypography>
                                </SoftBox>
                                <SoftBox display="flex" alignItems="center" gap={0.5}>
                                  {isDraftCard ? (
                                    <SoftButton
                                      iconOnly
                                      circular
                                      color="secondary"
                                      size="small"
                                      onClick={() => {
                                        setExpandedTurnoAdminId("");
                                        setSelectedTurnoAdminId("");
                                        setTurnoForm({ ...DEFAULT_TURNO_FORM });
                                        setFranjaForm({ ...DEFAULT_FRANJA_FORM });
                                        setFranjasDraftAdmin([]);
                                        setNewFranjaDraftAdmin({ ...DEFAULT_FRANJA_FORM });
                                      }}
                                    >
                                      <Icon sx={{ fontSize: "16px !important" }}>close</Icon>
                                    </SoftButton>
                                  ) : (
                                    <>
                                      <SoftButton
                                        iconOnly
                                        circular
                                        color="uproBlue"
                                        size="small"
                                        onClick={() => handleToggleTurnoAdminCard(turno)}
                                      >
                                        <Icon sx={{ fontSize: "16px !important" }}>edit</Icon>
                                      </SoftButton>
                                      <SoftButton
                                        iconOnly
                                        circular
                                        color="error"
                                        size="small"
                                        disabled={guardandoTurnosAdmin || isTurnoProtegido}
                                        onClick={() => handleDeleteTurnoAdmin(turno)}
                                      >
                                        <Icon sx={{ fontSize: "16px !important" }}>delete</Icon>
                                      </SoftButton>
                                    </>
                                  )}
                                </SoftBox>
                              </SoftBox>

                              {!isDraftCard && (
                                <SoftBox mt={1.2}>
                                  <SoftTypography variant="caption" color="secondary" display="block">
                                    Franja pedidos: {turno?.pedidoDesdeHora && turno?.pedidoHastaHora
                                      ? `desde ${toTimeInput(turno.pedidoDesdeHora)} (d${Number(
                                        turno?.pedidoDesdeOffsetDias || 0
                                      )}) hasta ${toTimeInput(turno.pedidoHastaHora)} (d${Number(
                                        turno?.pedidoHastaOffsetDias || 0
                                      )})`
                                      : "sin configurar"}
                                  </SoftTypography>
                                  <SoftTypography variant="caption" color="secondary" display="block" mt={0.5}>
                                    Franja de comida:
                                  </SoftTypography>
                                  {franjasLectura.length === 0 ? (
                                    <SoftTypography variant="caption" color="secondary" display="block">
                                      Sin franjas cargadas
                                    </SoftTypography>
                                  ) : (
                                    franjasLectura.map((franja) => (
                                      <SoftTypography key={franja.id} variant="caption" color="secondary" display="block">
                                        {toTimeInput(franja.horaInicio)} - {toTimeInput(franja.horaFin)}
                                      </SoftTypography>
                                    ))
                                  )}
                                </SoftBox>
                              )}

                              {isExpanded && (
                                <SoftBox mt={1.5} pt={1.5} sx={{ borderTop: "1px solid #e6e6e6" }}>
                                  <Grid container spacing={1}>
                                    <Grid item xs={12} md={3}>
                                      <SoftInput
                                        placeholder="Nombre"
                                        value={turnoForm.nombre}
                                        disabled={Boolean(turnoForm.protegido || turnoForm.bloqueadoPorUso)}
                                        onChange={(e) =>
                                          setTurnoForm((prev) => ({ ...prev, nombre: e.target.value }))
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                      <SoftInput
                                        type="time"
                                        value={turnoForm.horaInicio}
                                        onChange={(e) =>
                                          setTurnoForm((prev) => ({ ...prev, horaInicio: e.target.value }))
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                      <SoftInput
                                        type="time"
                                        value={turnoForm.horaFin}
                                        onChange={(e) =>
                                          setTurnoForm((prev) => ({ ...prev, horaFin: e.target.value }))
                                        }
                                      />
                                    </Grid>
                                    {mostrarSelectOrdenAdmin ? (
                                      <Grid item xs={6} md={2}>
                                        <Select
                                          fullWidth
                                          value={Number(turnoForm.orden || 1)}
                                          onChange={(e) =>
                                            setTurnoForm((prev) => ({
                                              ...prev,
                                              orden: Number(e.target.value),
                                            }))
                                          }
                                        >
                                          {opcionesOrdenAdmin.map((opcion) => (
                                            <MenuItem key={opcion} value={opcion}>
                                              {toOrdenLabel(opcion)}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </Grid>
                                    ) : null}
                                    <Grid item xs={6} md={mostrarSelectOrdenAdmin ? 3 : 5}>
                                      <SoftButton
                                        fullWidth
                                        color="uproGreen"
                                        onClick={handleSaveTurnoAdmin}
                                        disabled={guardandoTurnosAdmin}
                                      >
                                        Guardar turno
                                      </SoftButton>
                                    </Grid>
                                  </Grid>

                                  <Grid container spacing={1} mt={0.4}>
                                    {turnoForm.protegido ? (
                                      <Grid item xs={12}>
                                        <SoftTypography variant="caption" color="info" display="block">
                                          Turno de sistema protegido: no puede cambiarse el nombre ni eliminarse.
                                        </SoftTypography>
                                      </Grid>
                                    ) : null}
                                    {turnoForm.bloqueadoPorUso ? (
                                      <Grid item xs={12}>
                                        <SoftTypography variant="caption" color="info" display="block">
                                          Turno con menus historicos: no puede cambiarse el nombre ni desactivarse.
                                        </SoftTypography>
                                      </Grid>
                                    ) : null}
                                    <Grid item xs={12}>
                                      <SoftTypography variant="caption" color="secondary" display="block">
                                        Franja de pedidos (si inicio mayor que fin, cruza medianoche)
                                      </SoftTypography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                      <SoftInput
                                        type="time"
                                        value={turnoForm.pedidoDesdeHora}
                                        onChange={(e) =>
                                          setTurnoForm((prev) => ({ ...prev, pedidoDesdeHora: e.target.value }))
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                      <SoftInput
                                        value={cruzaMedianocheVentanaPedidos ? "Dia anterior" : "Mismo dia"}
                                        readOnly
                                      />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                      <SoftInput
                                        type="time"
                                        value={turnoForm.pedidoHastaHora}
                                        onChange={(e) =>
                                          setTurnoForm((prev) => ({ ...prev, pedidoHastaHora: e.target.value }))
                                        }
                                      />
                                    </Grid>
                                  </Grid>

                                  {isDraftCard ? (
                                    <SoftTypography variant="caption" color="secondary" display="block" mt={1}>
                                      Luego de guardar el turno podra configurar franjas de comida.
                                    </SoftTypography>
                                  ) : (
                                    <>
                                      <Divider sx={{ my: 1.5 }} />

                                      <SoftTypography variant="caption" color="secondary" display="block" mb={1}>
                                        Franjas de comida
                                      </SoftTypography>
                                      {loadingFranjasAdmin ? (
                                        <SoftBox py={2} display="flex" alignItems="center" justifyContent="center">
                                          <CircularProgress size={20} />
                                        </SoftBox>
                                      ) : franjasDraftAdmin.length === 0 ? (
                                        <SoftTypography variant="caption" color="secondary" display="block" mb={1}>
                                          No hay franjas cargadas para este turno.
                                        </SoftTypography>
                                      ) : (
                                        franjasDraftAdmin.map((franja) => (
                                          <Grid container spacing={1} key={franja.id} alignItems="center" mb={0.4}>
                                            <Grid item xs={5} md={4.5}>
                                              <SoftInput
                                                type="time"
                                                value={franja.horaInicio}
                                                onChange={(e) =>
                                                  handleFranjaDraftChange(franja.id, "horaInicio", e.target.value)
                                                }
                                              />
                                            </Grid>
                                            <Grid item xs={5} md={4.5}>
                                              <SoftInput
                                                type="time"
                                                value={franja.horaFin}
                                                onChange={(e) =>
                                                  handleFranjaDraftChange(franja.id, "horaFin", e.target.value)
                                                }
                                              />
                                            </Grid>
                                            <Grid item xs={6} md={1.5}>
                                              <SoftButton
                                                fullWidth
                                                color="uproGreen"
                                                disabled={guardandoFranjasAdmin}
                                                onClick={() => handleSaveFranjaDraft(franja.id)}
                                              >
                                                <Icon sx={{ fontSize: "16px !important" }}>save</Icon>
                                              </SoftButton>
                                            </Grid>
                                            <Grid item xs={6} md={1.5}>
                                              <SoftButton
                                                fullWidth
                                                color="error"
                                                disabled={guardandoFranjasAdmin}
                                                onClick={() => handleDeleteFranjaAdmin(franja)}
                                              >
                                                <Icon sx={{ fontSize: "16px !important" }}>delete</Icon>
                                              </SoftButton>
                                            </Grid>
                                          </Grid>
                                        ))
                                      )}

                                      <Grid container spacing={1} alignItems="center" mt={0.5}>
                                        <Grid item xs={5} md={4.5}>
                                          <SoftInput
                                            type="time"
                                            value={newFranjaDraftAdmin.horaInicio}
                                            onChange={(e) =>
                                              setNewFranjaDraftAdmin((prev) => ({
                                                ...prev,
                                                horaInicio: e.target.value,
                                              }))
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={5} md={4.5}>
                                          <SoftInput
                                            type="time"
                                            value={newFranjaDraftAdmin.horaFin}
                                            onChange={(e) =>
                                              setNewFranjaDraftAdmin((prev) => ({
                                                ...prev,
                                                horaFin: e.target.value,
                                              }))
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                          <SoftButton
                                            fullWidth
                                            color="dark"
                                            disabled={guardandoFranjasAdmin}
                                            onClick={handleCreateFranjaForExpandedTurno}
                                          >
                                            <Icon sx={{ fontSize: "16px !important" }}>add</Icon>&nbsp;Agregar franja
                                          </SoftButton>
                                        </Grid>
                                      </Grid>
                                    </>
                                  )}
                                </SoftBox>
                              )}
                            </SoftBox>
                          );
                        })
                      )}
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Card sx={{ p: 2 }}>
                      <SoftTypography variant="button" fontWeight="bold">
                        Configuracion de pedidos
                      </SoftTypography>
                      <SoftBox mt={1.5}>
                        <SoftTypography variant="caption" color="secondary">
                          Maximo de pedidos por usuario por dia
                        </SoftTypography>
                        <SoftInput
                          type="number"
                          value={configPedidosForm.maxPedidosUsuarioPorDia}
                          onChange={(e) =>
                            setConfigPedidosForm((prev) => ({
                              ...prev,
                              maxPedidosUsuarioPorDia: e.target.value,
                            }))
                          }
                        />
                      </SoftBox>
                      <SoftBox mt={2} display="flex" justifyContent="end">
                        <SoftButton
                          color="dark"
                          onClick={handleSaveConfigPedidosAdmin}
                          disabled={loadingSaveConfigAdmin || loadingConfigAdmin}
                        >
                          {loadingSaveConfigAdmin ? "Guardando..." : "Guardar configuracion"}
                        </SoftButton>
                      </SoftBox>
                    </Card>
                  </Grid>
                </Grid>
              </Card>
            </Fade>
          </Modal>
          <Modal
            open={openModalResumenVentasLocalidad}
            onClose={() => setOpenModalResumenVentasLocalidad(false)}
            closeAfterTransition
            aria-labelledby="modal-resumen-ventas-localidad-title"
            aria-describedby="modal-resumen-ventas-localidad-description"
          >
            <Fade in={openModalResumenVentasLocalidad}>
              <Card sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: '80%',
                maxWidth: '550px',
                maxHeight: '90vh',
                overflow: 'auto',
                p: 3,
              }}>
                <SoftBox display="flex" gap={1} alignItems="center" justifyContent="space-between" mb={3}>
                  <SoftBox display="flex" alignItems="center" gap={1}>
                    <Icon fontSize="medium" color="info">analytics</Icon>
                    <SoftTypography variant="h6">
                      Resumen de Ventas por Localidad
                    </SoftTypography>
                  </SoftBox>
                  <SoftButton
                    variant="gradient"
                    color="secondary"
                    onClick={() => setOpenModalResumenVentasLocalidad(false)}
                    circular
                    fullWidth
                    iconOnly
                  >
                    <Icon>close</Icon>
                  </SoftButton>
                </SoftBox>

                {/* Filtros */}
                <SoftBox mb={3}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <SoftTypography variant="body2" fontWeight="medium" mb={1}>
                        Fecha Inicio:
                      </SoftTypography>
                      <SoftInput
                        type="date"
                        value={filtrosResumenVentas.fechaInicio}
                        onChange={(e) => setFiltrosResumenVentas(prev => ({
                          ...prev,
                          fechaInicio: e.target.value
                        }))}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <SoftTypography variant="body2" fontWeight="medium" mb={1}>
                        Fecha Fin:
                      </SoftTypography>
                      <SoftInput
                        type="date"
                        value={filtrosResumenVentas.fechaFin}
                        onChange={(e) => setFiltrosResumenVentas(prev => ({
                          ...prev,
                          fechaFin: e.target.value
                        }))}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <SoftTypography variant="body2" fontWeight="medium" mb={1}>
                        &nbsp;
                      </SoftTypography>
                      <SoftButton
                        variant="gradient"
                        color="info"
                        onClick={obtenerResumenVentas}
                        disabled={loadingResumenVentas}
                        fullWidth
                      >
                        <Icon>search</Icon>&nbsp;Buscar
                      </SoftButton>
                    </Grid>
                  </Grid>
                </SoftBox>

                {/* Contenido */}
                <SoftBox mb={2}>
                  {loadingResumenVentas ? (
                    <Loading />
                  ) : resumenVentasData.length > 0 ? (
                    <Table
                      columns={[
                        { name: "fechaPedido", desc: "Fecha", align: "center", width: "25%", align: "center" },
                        { name: "destino", desc: "Destino", align: "left", width: "auto", align: "center" },
                        { name: "total", desc: "Cantidad de Pedidos", align: "center", width: "30%", align: "center" }
                      ]}
                      rows={(resumenVentasData || []).map(item => ({
                        fechaPedido: (
                          <SoftTypography
                            verticalAlign="text-top"
                            variant="button"
                            color="dark"
                            fontWeight="bold"
                          >
                            {dayjs(item.fechaPedido).format('DD/MM/YYYY')}
                          </SoftTypography>
                        ),
                        destino: (
                          <SoftTypography
                            variant="button"
                            color="secondary"
                            fontWeight="regular"
                          >
                            {item.destino ? item.destino : ""}
                          </SoftTypography>
                        ),
                        total: (
                          <SoftTypography
                            variant="button"
                            color="secondary"
                            fontWeight="regular"
                          >
                            {item.total ? item.total : ""}
                          </SoftTypography>
                        ),
                      }))}
                    />
                  ) : (
                    <SinDatos />
                  )}
                </SoftBox>

                {/* Resumen totales */}
                {resumenVentasData.length > 0 && (
                  <SoftBox mb={2} p={2} sx={{ backgroundColor: 'grey.100', borderRadius: 1 }}>
                    <SoftTypography variant="h6" fontWeight="medium">
                      Total de pedidos: {resumenVentasData.reduce((total, item) => total + item.total, 0)}
                    </SoftTypography>
                  </SoftBox>
                )}
              </Card>
            </Fade>
          </Modal>
          <Modal
            open={openModalFactura}
            onClose={handleCloseFacturaModal}
            closeAfterTransition
            aria-labelledby="modal-factura-title"
            aria-describedby="modal-factura-description"
          >
            <Fade in={openModalFactura}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "80%",
                  maxHeight: "90%",
                  overflowY: "auto",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  p: 4,
                }}
              >
                <Card>
                  {facturaData && empresaData && (
                    <FacturaImpresion factura={facturaData} empresa={empresaData} />
                  )}
                </Card>
              </Box>
            </Fade>
          </Modal>
          <ModalExcel open={openModalExcel} handleClose={handleCloseModalExcel} />
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
}

Pedidos.propTypes = {
  setRefetchPedidos: PropTypes.func,
};

export default Pedidos;
